// src/utils/exportProject.js
// Async, concurrency-limited exporter that mirrors CharacterTile / SceneTile data.
// Produces a user-friendly ZIP you specified.

import { getScreenplay, getScenes, getCharacterBible } from '../services/db';
import { listArtifacts } from '../services/ArtifactStore';
import { renderDetailedScreenplayText } from './utility';

// Lazy-load JSZip to keep the main bundle slim
let JSZipLib = null;
async function getJSZip() {
  if (!JSZipLib) {
    const mod = await import('jszip');
    JSZipLib = mod.default || mod;
  }
  return JSZipLib;
}

const ILLEGAL = /[<>:"/\\|?*\x00-\x1F]/g;
const pad2 = (n) => String(n).padStart(2, '0');
const safe = (s, fb = 'untitled') => {
  const v = String(s || '').replace(ILLEGAL, ' ').replace(/\s+/g, ' ').trim();
  return v || fb;
};

function guessExt(url = '') {
  const m = url.toLowerCase().match(/\.(png|jpg|jpeg|webp|gif|mp4|webm|mov|m4v|mp3|m4a|wav|ogg)(?:[?#].*)?$/i);
  return m ? `.${m[1].replace('jpeg', 'jpg')}` : '';
}
function extFromCT(ct = '') {
  const t = (ct || '').toLowerCase();
  if (t.includes('image/png')) return '.png';
  if (t.includes('image/jpeg')) return '.jpg';
  if (t.includes('image/webp')) return '.webp';
  if (t.includes('video/mp4')) return '.mp4';
  if (t.includes('video/webm')) return '.webm';
  if (t.includes('video/quicktime')) return '.mov';
  if (t.includes('audio/mpeg') || t.includes('audio/mp3')) return '.mp3';
  if (t.includes('audio/wav')) return '.wav';
  if (t.includes('audio/ogg')) return '.ogg';
  if (t.includes('audio/mp4')) return '.m4a';
  return '';
}

function dataUrlToBuffer(dataUrl) {
  const [head, b64] = String(dataUrl).split(',');
  const mime = (head.match(/^data:([^;]+)/) || [, 'application/octet-stream'])[1];
  const bin = atob(b64 || '');
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return { buffer: bytes.buffer, ext: extFromCT(mime) || '' };
}

// Helper to get API URL
const getApiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:5000';

async function fetchFile(url) {
  if (!url) return null;
  if (url.startsWith('data:')) return dataUrlToBuffer(url);

  // 1. Try direct fetch
  try {
    // Fix: GCS public buckets reject 'include' credentials with wildcard error.
    // We omit credentials for direct media fetches.
    let res = await fetch(url, { method: 'GET', credentials: 'omit' });
    if (res.ok) {
      const ct = res.headers.get('content-type') || '';
      const ab = await res.arrayBuffer();
      return { buffer: ab, ext: extFromCT(ct) || guessExt(url) || '' };
    }
  } catch (err) {
    // CORS error or network error - proceed to fallback
    // console.warn(`Direct fetch failed for ${url}, trying proxy...`);
  }

  // 2. Fallback to Proxy
  try {
    const proxyUrl = `${getApiUrl()}/api/proxy?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl);
    if (!res.ok) return null;

    const ct = res.headers.get('content-type') || '';
    const ab = await res.arrayBuffer();
    return { buffer: ab, ext: extFromCT(ct) || guessExt(url) || '' };
  } catch (err) {
    console.error(`Proxy fetch also failed for ${url}`, err);
    return null;
  }
}

async function runPool(items, limit, worker) {
  const out = new Array(items.length);
  let i = 0;
  const step = async () => {
    while (i < items.length) {
      const idx = i++;
      try { out[idx] = await worker(items[idx], idx); } catch { out[idx] = null; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, step));
  return out;
}

function makeReadme(projectTitle) {
  return [
    `${projectTitle}`,
    ``,
    `Thanks for using Alekhine AI!`,
    ``,
    `What's inside:`,
    `- Story/story.txt`,
    `- Screenplay/screenplay.txt`,
    `- Characters/<Character Name>/character.txt + image`,
    `- Scenes/Scene NN - <Title>/* (scene.txt, image, video, Audio/, Music/)`,
    `- Additional Artifacts/(Images|Videos|Audio)`,
    `- Final Movie/<Project Title> - Final.mp4 (if available)`,
  ].join('\n');
}

// === Text generation aligned with your tiles ===

// CharacterTile shows character.name and renders description by splitting on ":" per line.
// Export the exact description text verbatim, with a header line for name.
function characterTxt(name, details) {
  const lines = [];
  lines.push(`Name: ${name || ''}`);
  lines.push('');
  lines.push(String(details?.description || '').trim());
  return lines.join('\n');
}

// SceneTile fields:
//  title, duration, summary
//  additionalInfo.visual
//  additionalInfo.environment
//  additionalInfo.characterExperessions (array)  ← note spelling in your code
//  additionalInfo.dialogues[] = { text, audio }
//  additionalInfo.music = { text, audio }
function sceneTxt(scene, audioFileRefs = [], musicFileRef = null) {
  const a = scene?.additionalInfo || {};
  const lines = [];
  if (scene?.title) lines.push(`Title: ${scene.title}`);
  if (scene?.duration !== undefined && scene?.duration !== null) lines.push(`Duration: ${scene.duration}`);
  lines.push('');

  if (scene?.summary) {
    lines.push('Summary:');
    lines.push(String(scene.summary));
    lines.push('');
  }

  if (a.visual) {
    lines.push('Visual Prompt:');
    lines.push(String(a.visual));
    lines.push('');
  }

  if (a.environment) {
    lines.push('Environment:');
    lines.push(String(a.environment));
    lines.push('');
  }

  if (Array.isArray(a.characterExperessions) && a.characterExperessions.length) {
    lines.push('Character Emotions:');
    a.characterExperessions.forEach((v) => lines.push(`- ${String(v)}`));
    lines.push('');
  }

  if (Array.isArray(a.dialogues) && a.dialogues.length) {
    lines.push('Dialogues:');
    a.dialogues.forEach((d, i) => {
      const ref = audioFileRefs[i] ? ` (Audio: ${audioFileRefs[i]})` : '';
      lines.push(`- ${String(d?.text || '')}${ref}`);
    });
    lines.push('');
  }

  if (a.music?.text || musicFileRef) {
    lines.push('Background Music:');
    if (a.music?.text) lines.push(String(a.music.text));
    if (musicFileRef) lines.push(`File: ${musicFileRef}`);
    lines.push('');
  }

  return lines.join('\n');
}

// === Main export ===
export async function exportProjectZip(project, { onProgress } = {}) {
  const projectId = project?._id || project?.id;
  if (!projectId) throw new Error('exportProjectZip: missing project id');

  const title = safe(project?.title, 'Project');
  const Zip = await getJSZip();
  const zip = new Zip();

  const root = zip.folder(title);
  const storyDir = root.folder('Story');
  const screenplayDir = root.folder('Screenplay');
  const charsDir = root.folder('Characters');
  const scenesDir = root.folder('Scenes');
  const artifactsDir = root.folder('Additional Artifacts');
  const finalDir = root.folder('Final Movie');

  root.file('README.txt', makeReadme(title));

  // Story (if present on project)
  const storyText = (project?.story || project?.description || '').trim();
  if (storyText) storyDir.file('story.txt', storyText);

  // Screenplay
  let screenplay = '';
  try { screenplay = await getScreenplay(projectId); } catch { }
  const spText = (typeof screenplay === 'object') ? renderDetailedScreenplayText(screenplay) : String(screenplay);
  screenplayDir.file('screenplay.txt', spText || 'No screenplay available.');

  // Character Bible (Map or plain object)
  let cb = null;
  try { cb = await getCharacterBible(projectId); } catch { }
  const entries = cb && typeof cb.entries === 'function'
    ? Array.from(cb.entries())
    : cb ? Object.entries(cb) : [];

  await runPool(entries, 6, async ([name, details]) => {
    const dir = charsDir.folder(safe(name, 'Character'));
    // Text content: exact description + name header (matches CharacterTile)
    dir.file('character.txt', characterTxt(name, details));
    const imgUrl = details?.image || details?.imageUrl || details?.imageURL;
    if (imgUrl) {
      const got = await fetchFile(imgUrl);
      if (got) dir.file(`image${got.ext || guessExt(imgUrl) || '.png'}`, got.buffer);
    }
  });
  onProgress?.({ phase: 'characters', count: entries.length });

  // Scenes
  let scenes = [];
  try { scenes = await getScenes(projectId) || []; } catch { }
  await runPool(scenes, 4, async (scene, index) => {
    const base = `Scene ${pad2(index + 1)} - ${safe(scene?.title || `Scene ${index + 1}`)}`;
    const dir = scenesDir.folder(base);

    // Media
    const audioDir = dir.folder('Audio');
    const musicDir = dir.folder('Music');

    // Image
    if (scene?.image) {
      const got = await fetchFile(scene.image);
      if (got) dir.file(`image${got.ext || guessExt(scene.image) || '.png'}`, got.buffer);
    }

    // Video
    if (scene?.video) {
      const got = await fetchFile(scene.video);
      if (got) dir.file(`video${got.ext || guessExt(scene.video) || '.mp4'}`, got.buffer);
    }

    // Dialogues (array with .text and .audio)
    const a = scene?.additionalInfo || {};
    const audioRefs = [];
    if (Array.isArray(a.dialogues)) {
      await runPool(a.dialogues, 4, async (d, i) => {
        const u = d?.audio;
        if (!u) { audioRefs[i] = null; return; }
        const got = await fetchFile(u);
        if (!got) { audioRefs[i] = null; return; }
        const fname = `line-${pad2(i + 1)}${got.ext || guessExt(u) || '.mp3'}`;
        audioDir.file(fname, got.buffer);
        audioRefs[i] = `Audio/${fname}`;
      });
    }

    // Music
    let musicRef = null;
    if (a.music?.audio) {
      const got = await fetchFile(a.music.audio);
      if (got) {
        const fname = `track${got.ext || guessExt(a.music.audio) || '.mp3'}`;
        musicDir.file(fname, got.buffer);
        musicRef = `Music/${fname}`;
      }
    }

    // Scene text (aligned with SceneTile fields & labels)
    dir.file('scene.txt', sceneTxt(scene, audioRefs, musicRef));
  });
  onProgress?.({ phase: 'scenes', count: scenes.length });

  // Additional Artifacts (from ArtifactStore)
  let grouped = { image: [], video: [], audio: [] };
  try { grouped = await listArtifacts(projectId); } catch { }
  const addImg = artifactsDir.folder('Images');
  const addVid = artifactsDir.folder('Videos');
  const addAud = artifactsDir.folder('Audio');

  const nameArtifact = (a, i, defExt) => `${safe(a?.title || `artifact-${i + 1}`)}${guessExt(a?.outputUrl || a?.inputImageUrl || '') || defExt}`;

  await runPool(grouped.image || [], 6, async (a, i) => {
    const u = a?.outputUrl || a?.inputImageUrl;
    if (!u) return;
    const got = await fetchFile(u);
    if (got) addImg.file(nameArtifact(a, i, got.ext || '.png'), got.buffer);
  });

  await runPool(grouped.video || [], 3, async (a, i) => {
    const u = a?.outputUrl || a?.inputImageUrl;
    if (!u) return;
    const got = await fetchFile(u);
    if (got) addVid.file(nameArtifact(a, i, got.ext || '.mp4'), got.buffer);
  });

  await runPool(grouped.audio || [], 6, async (a, i) => {
    const u = a?.outputUrl;
    if (!u) return;
    const got = await fetchFile(u);
    if (got) addAud.file(nameArtifact(a, i, got.ext || '.mp3'), got.buffer);
  });
  onProgress?.({ phase: 'artifacts' });

  // Final movie (if available on project or artifacts)
  const finalUrl =
    project?.mergedVideoUrl ||
    project?.finalMovieUrl ||
    (Array.isArray(grouped.video) ? grouped.video.find(v => /final|merged/i.test(v?.title || ''))?.outputUrl : null);

  if (finalUrl) {
    const got = await fetchFile(finalUrl);
    if (got) finalDir.file(`${title} - Final${got.ext || '.mp4'}`, got.buffer);
  }

  // Build ZIP (async; UI remains interactive)
  onProgress?.({ phase: 'zipping' });
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  // Trigger download
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${title}.zip`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 2000);

  onProgress?.({ phase: 'done' });
}
